import { formatCustomerReceiptNumber } from '../../utils/formatCustomerReceiptNumber';
import { getReceiptLabel } from '../../utils/receiptLabels';
import { resolveSaleReceiptDate } from '../../utils/saleReceiptDateTime';
import {
  getSaleLineActiveAmount,
  getSaleLineActiveQuantity,
  getSaleLineUnitPrice
} from '../../domain/sales/saleLineMath';

function pickProducts(source) {
  if (Array.isArray(source?.products)) return source.products;
  if (Array.isArray(source?.items)) return source.items;
  if (Array.isArray(source?.lineItems)) return source.lineItems;
  return [];
}

function normalizeItem(item, language = 'fr') {
  const activeQty = getSaleLineActiveQuantity({
    ...item,
    quantity: item?.quantity ?? item?.qty ?? 0
  });
  const isPack =
    item?.sellMode === 'pack' &&
    Number(item?.sellingPackPrice) > 0 &&
    Number(item?.unitsPerPack) > 1;

  // Pack lines: show carton count × pack price so ticket matches shelf price (e.g. 1 × 110).
  if (isPack) {
    const unitsPerPack = Math.round(Number(item.unitsPerPack));
    const packCount = activeQty / unitsPerPack;
    const packPrice = Number(item.sellingPackPrice);
    const lineTotal = getSaleLineActiveAmount({
      ...item,
      quantity: item?.quantity ?? item?.qty ?? 0
    });
    return {
      name: item?.name || item?.productName || getReceiptLabel('defaultArticle', language),
      quantity: Number.isFinite(packCount) && packCount > 0 ? Math.round(packCount * 1000) / 1000 : activeQty,
      unitPrice: packPrice,
      lineTotal: Math.round(lineTotal * 100) / 100
    };
  }

  const unitPrice = getSaleLineUnitPrice(item) || Number(
    item?.price ?? item?.unitPrice ?? item?.sellingPrice ?? 0
  ) || 0;
  const lineTotal = Number(
    item?.lineTotal ??
    item?.total ??
    item?.amount ??
    getSaleLineActiveAmount({
      ...item,
      quantity: item?.quantity ?? item?.qty ?? 0
    })
  ) || 0;

  return {
    name: item?.name || item?.productName || getReceiptLabel('defaultArticle', language),
    quantity: activeQty,
    unitPrice,
    lineTotal: Math.round(lineTotal * 100) / 100
  };
}

/**
 * Maps sale / receipt business data to a printer-neutral view model.
 */
export function mapSaleToThermalReceiptViewModel(receiptData = {}, options = {}) {
  const business = receiptData.businessInfo || {};
  const language = options.language || 'fr';
  const products = pickProducts(receiptData).map((item) => normalizeItem(item, language));
  const itemCount = products.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const date = resolveSaleReceiptDate(receiptData, {
    useLiveTime: receiptData?.status === 'pending'
  });
  const orderId = receiptData.orderId || receiptData.id || '';
  const receiptNumber = options.merchantCopy || options.useCustomerReceiptNumber === false
    ? String(orderId)
    : formatCustomerReceiptNumber(orderId) || String(orderId);

  const paymentMethod = receiptData.paymentMethod || 'cash';
  const paymentLabel = paymentMethod === 'electronic'
    ? receiptData.electronicPaymentMethod || getReceiptLabel('paymentElectronic', language)
    : paymentMethod === 'cash'
      ? getReceiptLabel('paymentCash', language)
      : paymentMethod === 'pending' || paymentMethod === 'addition'
        ? getReceiptLabel('paymentPending', language)
        : String(paymentMethod);

  return {
    store: {
      name: business.name || receiptData.storeName || 'AMAN',
      address: business.address || receiptData.address || '',
      phone: business.phone || receiptData.phone || '',
      nif: business.nif || business.taxId || business.vatNumber || '',
      logoUrl: business.logo || receiptData.logo || ''
    },
    receipt: {
      title: options.title || getReceiptLabel(
        options.merchantCopy ? 'receiptTitleMerchant' : 'receiptTitleClient',
        language
      ),
      number: receiptNumber,
      date,
      barcode: receiptNumber,
      qrPayload: options.qrPayload || receiptNumber
    },
    cashier: {
      name: receiptData.workerName || receiptData.sellerName || getReceiptLabel('defaultSeller', language),
      show: options.showCashier !== false
    },
    customer: {
      name: receiptData.customerName || '',
      phone: receiptData.customerPhone || '',
      show: options.showCustomer !== false && !!(receiptData.customerName || receiptData.customerPhone)
    },
    location: {
      name: receiptData.location || receiptData.stockLocation || '',
      show: options.showLocation === true || options.merchantCopy === true
    },
    items: products,
    totals: {
      itemCount,
      subtotal: Number(receiptData.subtotal ?? 0) || 0,
      discount: Number(receiptData.discount ?? 0) || 0,
      subtotalAfterDiscount: Number(receiptData.subtotalAfterDiscount ?? receiptData.subtotal ?? 0) || 0,
      tax: Number(receiptData.tax ?? 0) || 0,
      total: Number(receiptData.total ?? 0) || 0,
      showVat: options.showVat === true && Number(receiptData.tax ?? 0) > 0
    },
    payment: {
      method: paymentLabel,
      cashAmount: Number(receiptData.cashAmount ?? 0) || 0,
      change: Number(receiptData.change ?? 0) || 0,
      isCash: paymentMethod === 'cash'
    },
    footerMessage: options.footerMessage || getReceiptLabel('footerDefault', language),
    flags: {
      showLogo: options.showLogo !== false && !!(business.logo || receiptData.logo),
      showNif: options.showNif === true && !!(business.nif || business.taxId || business.vatNumber),
      showBarcode: options.showBarcode !== false,
      showQrCode: options.showQrCode === true,
      showItemCount: options.showItemCount !== false
    }
  };
}
